/**
 * Route Photo Service
 * Frontend service for crowdsourced turn-by-turn navigation photos
 */

import { telegram } from './telegram';

const API_BASE = '/api/route-photos';

export interface RoutePhoto {
  id: string;
  facility_id: string;
  facility_type: string;
  r2_key: string;
  thumbnail_key?: string;
  original_filename?: string;
  mime_type: string;
  file_size: number;
  sequence_number: number;
  description?: string;
  landmark_type?: string;
  photo_latitude?: number;
  photo_longitude?: number;
  distance_from_facility?: number;
  telegram_user_id: string;
  telegram_username?: string;
  upvotes: number;
  downvotes: number;
  score: number;
  status: string;
  created_at: string;
  thumbnailUrl: string;
  fullUrl: string;
  userUpvoted: boolean;
  userDownvoted: boolean;
}

export interface RoutePhotoUploadData {
  file: File;
  facilityId: string;
  facilityType?: string;
  sequenceNumber: number;
  description?: string;
  landmarkType?: string;
  latitude?: number;
  longitude?: number;
  facilityLat?: number;
  facilityLng?: number;
}

export const LANDMARK_TYPES = [
  { value: 'highway', label: '🛣️ Highway Exit/Turn', description: 'Turn off main highway' },
  { value: 'intersection', label: '🔀 Intersection', description: 'Road intersection or junction' },
  { value: 'gate', label: '🚧 Gate', description: 'Entrance gate' },
  { value: 'cattle_guard', label: '🐄 Cattle Guard', description: 'Cattle guard crossing' },
  { value: 'sign', label: '🪧 Sign', description: 'Road sign or marker' },
  { value: 'landmark', label: '📍 Landmark', description: 'Notable landmark' },
  { value: 'bridge', label: '🌉 Bridge/Crossing', description: 'Bridge or water crossing' },
  { value: 'turn', label: '↩️ Turn', description: 'Important turn' },
];

class RoutePhotoService {
  private getUserHeaders() {
    const user = telegram.getUser();
    return {
      'X-Telegram-User-Id': telegram.getUserId() || '',
      'X-Telegram-Username': user?.username || user?.first_name || '',
      'X-Telegram-Init-Data': telegram.getInitData()
    };
  }

  /**
   * Get route photos for a facility
   */
  async getRoutePhotos(facilityId: string, facilityType: string = 'well'): Promise<RoutePhoto[]> {
    try {
      const response = await fetch(
        `${API_BASE}?facilityId=${encodeURIComponent(facilityId)}&facilityType=${encodeURIComponent(facilityType)}`,
        { headers: this.getUserHeaders() }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch route photos');
      }
      
      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error('Error fetching route photos:', error);
      return [];
    }
  }

  /**
   * Get global route photos feed, optionally filtered by map bounds
   */
  async getGlobalRoutePhotos(bounds?: { north: number; south: number; east: number; west: number }, limit: number = 60): Promise<RoutePhoto[]> {
    try {
      const params = new URLSearchParams();
      params.set('limit', limit.toString());
      if (bounds) {
        params.set('north', bounds.north.toString());
        params.set('south', bounds.south.toString());
        params.set('east', bounds.east.toString());
        params.set('west', bounds.west.toString());
      }
      const response = await fetch(`${API_BASE}?${params.toString()}`, {
        headers: this.getUserHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch global route photos');
      }
      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error('Error fetching global route photos:', error);
      return [];
    }
  }

  /**
   * Upload a route photo
   */
  async uploadRoutePhoto(data: RoutePhotoUploadData): Promise<{ success: boolean; photoId?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('photo', data.file);
      formData.append('facilityId', data.facilityId);
      formData.append('facilityType', data.facilityType || 'well');
      formData.append('sequenceNumber', data.sequenceNumber.toString());
      
      if (data.description) {
        formData.append('description', data.description);
      }
      
      if (data.landmarkType) {
        formData.append('landmarkType', data.landmarkType);
      }
      
      if (data.latitude) {
        formData.append('latitude', data.latitude.toString());
      }
      
      if (data.longitude) {
        formData.append('longitude', data.longitude.toString());
      }

      if (data.facilityLat) {
        formData.append('facilityLat', data.facilityLat.toString());
      }

      if (data.facilityLng) {
        formData.append('facilityLng', data.facilityLng.toString());
      }

      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Upload failed' };
      }
      
      return { success: true, photoId: result.photoId };
    } catch (error) {
      console.error('Error uploading route photo:', error);
      return { success: false, error: 'Upload failed' };
    }
  }

  /**
   * Delete a route photo
   */
  async deleteRoutePhoto(photoId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/${photoId}`, {
        method: 'DELETE',
        headers: this.getUserHeaders()
      });
      return response.ok;
    } catch (error) {
      console.error('Error deleting route photo:', error);
      return false;
    }
  }

  /**
   * Vote on a route photo
   */
  async vote(photoId: string, voteType: 'up' | 'down' | 'none'): Promise<{
    success: boolean;
    upvotes: number;
    downvotes: number;
    score: number;
    userVote: 'up' | 'down' | null;
  } | null> {
    try {
      const response = await fetch(`${API_BASE}/vote`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photoId, voteType })
      });

      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch (error) {
      console.error('Error voting on route photo:', error);
      return null;
    }
  }

  /**
   * Get the next available sequence number for a facility
   */
  async getNextSequenceNumber(facilityId: string, facilityType: string = 'well'): Promise<number> {
    const photos = await this.getRoutePhotos(facilityId, facilityType);
    if (photos.length === 0) return 1;
    const maxSeq = Math.max(...photos.map(p => p.sequence_number));
    return maxSeq + 1;
  }

  /**
   * Format distance for display
   */
  formatDistance(meters: number | undefined | null): string {
    if (!meters) return '';
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    }
    const miles = meters / 1609.34;
    return `${miles.toFixed(1)} mi`;
  }

  /**
   * Get landmark type label
   */
  getLandmarkLabel(type: string | undefined): string {
    const landmark = LANDMARK_TYPES.find(l => l.value === type);
    return landmark?.label || type || '';
  }
}

export const routePhotoService = new RoutePhotoService();
export default routePhotoService;
